import {useMemo, useState} from 'react';
import {AwButton, AwCalendarGrid, AwErrorState, AwLoadingState, type AwCalendarCell} from '@ascension-tracker/ascension-ui';
import {useProjectTasks, type Task} from '../../hooks/useTasks.js';

function toDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}
function dueDateKey(value: string | null) {
  if (!value) return null;
  return String(value).slice(0, 10);
}

function buildMonthCells(year: number, month: number): AwCalendarCell[] {
  const firstOfMonth = new Date(year, month, 1);
  const start = new Date(firstOfMonth);
  start.setDate(start.getDate() - firstOfMonth.getDay());
  const todayKey = toDateKey(new Date());
  const cells: AwCalendarCell[] = [];
  for (let index = 0; index < 42; index++) {
    const date = new Date(start);
    date.setDate(start.getDate() + index);
    const key = toDateKey(date);
    cells.push({date: key, label: date.getDate(), inMonth: date.getMonth() === month, isToday: key === todayKey});
  }
  return cells;
}

export function CalendarView({projectId, onOpenTask}: {projectId: string; onOpenTask: (taskId: string) => void}) {
  const tasks = useProjectTasks(projectId);
  const [cursor, setCursor] = useState(() => { const now = new Date(); return {year: now.getFullYear(), month: now.getMonth()}; });
  const cells = useMemo(() => buildMonthCells(cursor.year, cursor.month), [cursor.year, cursor.month]);

  if (tasks.error) return <AwErrorState>{(tasks.error as Error).message}</AwErrorState>;
  if (!tasks.data) return <AwLoadingState/>;

  const byDate = new Map<string, Task[]>();
  for (const task of tasks.data) {
    const key = dueDateKey(task.dueOn);
    if (!key) continue;
    byDate.set(key, [...(byDate.get(key) ?? []), task]);
  }
  const monthLabel = new Date(cursor.year, cursor.month, 1).toLocaleDateString(undefined, {month: 'long', year: 'numeric'});

  function shiftMonth(delta: number) {
    setCursor(current => {
      const next = new Date(current.year, current.month + delta, 1);
      return {year: next.getFullYear(), month: next.getMonth()};
    });
  }

  return (
    <div className="calendar-view">
      <div className="calendar-view__controls">
        <AwButton type="button" variant="secondary" onClick={() => shiftMonth(-1)}>← Prev</AwButton>
        <strong>{monthLabel}</strong>
        <AwButton type="button" variant="secondary" onClick={() => shiftMonth(1)}>Next →</AwButton>
      </div>
      <AwCalendarGrid
        cells={cells}
        renderCell={cell => (
          <>
            {(byDate.get(cell.date) ?? []).map(task => (
              <button key={task.id} type="button" className={`calendar-task${task.completedAt ? ' is-complete' : ''}`} onClick={() => onOpenTask(task.id)}>{task.title}</button>
            ))}
          </>
        )}
      />
    </div>
  );
}
